import React, { useEffect, useRef, useState } from "react";
import "./banner.scss";
import {
  useUploadBannersMutation,
  useBannersQuery,
  useUpdateBannersMutation,
  useDeleteBannerMutation,
} from "../../../Services/vehicle/Vehicle";
import { toast } from "react-toastify";
import EditBanner from "./EditBanner";
import DeleteConfirmationModal from "./DeleteConfirmationModal"; // Import the separate modal component

const Banner = () => {
  const [title, setTitle] = useState("");
  const [mobileBanner, setMobileBanner] = useState<File | null>(null);
  const [webBanner, setWebBanner] = useState<File | null>(null);
  const [mobilePreview, setMobilePreview] = useState<string | null>(null);
  const [webPreview, setWebPreview] = useState<string | null>(null);
  const [getBanners, setGetBanners] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<any>(null);
  const [error, setError] = useState(false);

  // Delete confirmation modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<any>(null);

  const mobileFileRef = useRef<HTMLInputElement>(null);
  const webFileRef = useRef<HTMLInputElement>(null);

  const [bannerUploadTrigger] = useUploadBannersMutation();
  const [updatebannerTrigger] = useUpdateBannersMutation();
  const { data: bannerData, isLoading, refetch } = useBannersQuery({});
  const [deleteTrigger] = useDeleteBannerMutation();

  useEffect(() => {
    if (bannerData) {
      setGetBanners(bannerData?.results);
    }
  }, [bannerData]);

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "mobile" | "web"
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === "mobile") {
        setMobileBanner(file);
        setMobilePreview(URL.createObjectURL(file));
      } else {
        setWebBanner(file);
        setWebPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError(true);
      return;
    }

    if (!mobileBanner && !webBanner) {
      alert("Please select both banner image.");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("is_active", "true");

    if (mobileBanner) {
      formData.append("image", mobileBanner);
    }
    if (webBanner) {
      formData.append("web_image", webBanner);
    }

    try {
      const response = await bannerUploadTrigger(formData);
      if (response?.data) {
        toast.success("Banner uploaded successfully");
        setTitle("");
        setMobileBanner(null);
        setWebBanner(null);
        setMobilePreview(null);
        setWebPreview(null);
        if (mobileFileRef.current) mobileFileRef.current.value = "";
        if (webFileRef.current) webFileRef.current.value = "";
        refetch();
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (banner: any) => {
    setBannerToDelete(banner);
    setDeleteModalOpen(true);
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    if (!bannerToDelete) return;

    try {
      const response = await deleteTrigger(bannerToDelete.id);
      if (response?.data) {
        toast.success("Banner deleted successfully");
        refetch();
      } else {
        toast.error("Delete failed");
      }
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Something went wrong while deleting");
    } finally {
      // Reset delete modal state
      setBannerToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setBannerToDelete(null);
    setDeleteModalOpen(false);
  };

  return (
    <div className="banner-container" style={{ padding: "10px 0px" }}>
      <h2 className="banner-title" style={{ fontFamily: "Karbon-medium" }}>
        Upload Banner
      </h2>

      <form onSubmit={handleSubmit} className="banner-form">
        {/* Title Input */}
        <input
          type="text"
          placeholder="Enter title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="banner-input"
          style={{
            width: "30%",
            padding: "10px",
            border: error ? "1px solid red" : "1px solid #d3d0d0",
            background: "transparent",
            fontFamily: "Karbon-medium",
            borderRadius: "20px",
          }}
        />

        {/* Mobile Banner Upload */}
        <h4 style={{ marginTop: "20px", fontFamily: "Karbon-medium" }}>
          Upload for Mobile (600X300)
        </h4>
        <input
          type="file"
          accept="image/*"
          ref={mobileFileRef}
          onChange={(e) => handleImageChange(e, "mobile")}
          style={{ display: "none" }}
        />
        <div
          onClick={() => mobileFileRef.current?.click()}
          style={{
            border: "2px dashed #ccc",
            padding: mobilePreview ? "0" : "20px",
            borderRadius: "10px",
            textAlign: "center",
            cursor: "pointer",
            marginTop: "10px",
            background: "#f9f9f9",
            fontFamily: "Karbon-medium",
            width: mobilePreview ? "100%" : "30%",
          }}
        >
          {mobilePreview ? (
            <img
              src={mobilePreview}
              alt="Mobile Banner Preview"
              style={{
                width: "100%",
                borderRadius: "10px",
                maxHeight: "300px",
              }}
            />
          ) : (
            "Click here to select mobile banner"
          )}
        </div>

        {/* Web Banner Upload */}
        <h4 style={{ marginTop: "20px", fontFamily: "Karbon-medium" }}>
          Upload for Web (1960X640)
        </h4>
        <input
          type="file"
          accept="image/*"
          ref={webFileRef}
          onChange={(e) => handleImageChange(e, "web")}
          style={{ display: "none" }}
        />
        <div
          onClick={() => webFileRef.current?.click()}
          style={{
            border: "2px dashed #ccc",
            padding: webPreview ? "0" : "20px",
            borderRadius: "10px",
            textAlign: "center",
            cursor: "pointer",
            marginTop: "10px",
            background: "#f9f9f9",
            fontFamily: "Karbon-medium",
            width: webPreview ? "100%" : "30%",
          }}
        >
          {webPreview ? (
            <img
              src={webPreview}
              alt="Web Banner Preview"
              style={{
                width: "100%",
                borderRadius: "10px",
                maxHeight: "300px",
              }}
            />
          ) : (
            "Click here to select web banner"
          )}
        </div>

        {/* Submit Button */}
        <button
          disabled={!mobileBanner || !webBanner}
          type="submit"
          className="banner-button"
          style={{
            fontFamily: "Karbon-medium",
            background: !mobileBanner || !webBanner ? "grey" : "#D9490C",
            color: "white",
            border: "none",
            borderRadius: "20px",
            padding: "10px",
            fontSize: "16px",
            marginTop: "20px",
            width: "30%",
            cursor: !mobileBanner || !webBanner ? "not-allowed" : "pointer",
          }}
        >
          Upload Banner
        </button>
      </form>

      {/* Table */}
      <div className="table-wrapper">
        <h3 className="table-title">Banners</h3>
        <table className="banner-table">
          <thead style={{ fontFamily: "Karbon-medium" }}>
            <tr>
              <th>Sr.No.</th>
              <th>Title</th>
              <th>Mobile Image</th>
              <th>Web Image</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4}>Loading...</td>
              </tr>
            ) : getBanners?.length > 0 ? (
              getBanners.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{index + 1}</td>
                  <td>{item.title}</td>
                  <td>
                    <img
                      src={item.image}
                      alt={item.title}
                      className="table-image"
                      style={{ maxHeight: "60px" }}
                    />
                  </td>
                  <td>
                    <img
                      src={item.web_image}
                      alt={item.title}
                      className="table-image"
                      style={{ maxHeight: "60px" }}
                    />
                  </td>
                  <td>
                    <span
                      onClick={() => {
                        setSelectedBanner(item);
                        setEditModalOpen(true);
                      }}
                    >
                      <i
                        className="bi bi-pencil"
                        style={{ fontSize: "20px", cursor: "pointer" }}
                      ></i>
                    </span>
                    <span onClick={() => handleDeleteClick(item)}>
                      <i
                        className="bi bi-trash3"
                        style={{
                          fontSize: "20px",
                          cursor: "pointer",
                          marginLeft: "10px",
                        }}
                      ></i>
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>No banners found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {selectedBanner && (
        <EditBanner
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          initialTitle={selectedBanner.title}
          initialImage={selectedBanner.image}
          webImage={selectedBanner.web_image}
          onSubmit={async (formData) => {
            try {
              const response = await updatebannerTrigger({
                body: formData,
                id: selectedBanner.id,
              });

              if ("data" in response) {
                toast.success("Banner updated successfully");
                refetch();
              } else {
                toast.error("Update failed");
              }
            } catch {
              toast.error("Error occurred");
            }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Banner"
        itemName={bannerToDelete?.title}
        message={`Are you sure you want to delete the banner "${bannerToDelete?.title}"?`}
      />
    </div>
  );
};

export default Banner;
